import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { ArrowLeft, ArrowRight, RotateCw, Globe } from "lucide-react";
import { useNativeBrowserGeometry } from "../../hooks/useNativeBrowserGeometry";
import { hideNative, isTauri, onNativeNavigated, reloadNative } from "../../services/nativeBrowser";
import {
  browserStore,
  navigateBrowser,
  browserGoBack,
  browserGoForward,
  resolveBrowserSrc,
} from "../../stores/browserStore";

export function BrowserPane() {
  const state = browserStore.useStore();
  const [draft, setDraft] = useState(state.address);
  const [refreshKey, setRefreshKey] = useState(0);
  const toolbarRef = useRef<HTMLFormElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  // Keep the address input in sync when back/forward (or a launch) changes the
  // store address out from under the local draft.
  useEffect(() => {
    setDraft(state.address);
  }, [state.address]);

  const resolved = useMemo(
    () => resolveBrowserSrc(state.address, state.projectId),
    [state.address, state.projectId]
  );
  const nativeSrc = resolved.kind === "iframe" ? (resolved.nativeSrc ?? null) : null;
  const native = Boolean(isTauri() && nativeSrc);
  useNativeBrowserGeometry(native, bodyRef, nativeSrc, toolbarRef);

  useEffect(() => {
    if (!native) {
      void hideNative();
      return undefined;
    }
    let active = true;
    let dispose: (() => void) | undefined;
    void onNativeNavigated((url) => {
      if (url !== browserStore.getSnapshot().address) navigateBrowser(url);
    }).then((unlisten) => {
      // If the effect already cleaned up before the listener resolved,
      // unsubscribe immediately instead of leaking it.
      if (active) {
        dispose = unlisten;
      } else {
        unlisten();
      }
    });
    return () => {
      active = false;
      dispose?.();
    };
  }, [native]);

  // Derived nav state from the reactive store (useStore re-renders on change).
  const canBack = state.history.index > 0;
  const canForward = state.history.index < state.history.entries.length - 1;

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    navigateBrowser(draft);
  }

  // Cache-bust on reload without losing query params the resolver may add.
  // Computed inside the iframe branch below so branching stays on
  // `resolved.kind` alone — that keeps the discriminated union narrowing intact.
  function bust(src: string): string {
    return `${src}${src.includes("?") ? "&" : "?"}r=${refreshKey}`;
  }

  return (
    <div className="browser-pane">
      <form className="browser-pane__toolbar" onSubmit={onSubmit} ref={toolbarRef}>
        <button
          type="button"
          className="browser-pane__btn"
          aria-label="Back"
          disabled={!canBack}
          onClick={() => browserGoBack()}
        >
          <ArrowLeft size={14} />
        </button>
        <button
          type="button"
          className="browser-pane__btn"
          aria-label="Forward"
          disabled={!canForward}
          onClick={() => browserGoForward()}
        >
          <ArrowRight size={14} />
        </button>
        <button
          type="button"
          className="browser-pane__btn"
          aria-label="Reload"
          onClick={() => {
            if (native) {
              void reloadNative();
            } else {
              setRefreshKey((k) => k + 1);
            }
          }}
        >
          <RotateCw size={14} />
        </button>
        <input
          className="browser-pane__address"
          aria-label="Address"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Path, localhost URL, or web address"
          spellCheck={false}
        />
      </form>
      <div className="browser-pane__body" ref={bodyRef}>
        {resolved.kind === "iframe" && native ? (
          <div
            className="browser-pane__native-placeholder"
            data-testid="native-browser-placeholder"
          >
            <Globe size={28} aria-hidden />
            <p>Native browser active</p>
            <span>{nativeSrc}</span>
          </div>
        ) : resolved.kind === "iframe" ? (
          <iframe
            key={bust(resolved.src)}
            title="Browser"
            src={bust(resolved.src)}
            // NEVER `allow-scripts allow-same-origin` together — that pair lets
            // the framed document remove its own sandbox, and the HTML spec
            // says so outright. Project previews are served from OUR origin
            // (/api/preview/file/… and /api/preview/dev/…), so granting
            // allow-same-origin handed agent-written project HTML the app's
            // own origin: window.parent.Clerk, the app's cookies and storage,
            // every /api/* route with the user's bearer, and the Clerk relay.
            // Without it the document gets an OPAQUE origin and can reach none
            // of that, while the iframe's own load request still carries the
            // session cookie, so the preview keeps rendering and staying
            // authenticated. What it loses is storage of its own — acceptable
            // for previewing project output, and the desktop shell routes dev
            // servers to the native browser anyway (see `native` above).
            sandbox={
              resolved.external
                ? "allow-scripts allow-popups allow-forms"
                : "allow-scripts allow-forms"
            }
            className="browser-pane__iframe"
          />
        ) : (
          <div className="browser-pane__empty">
            <Globe size={28} aria-hidden />
            <p>{resolved.reason}</p>
          </div>
        )}
      </div>
    </div>
  );
}
